import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getLabel} from "@mytour/shared";
import type {CertificatRamonagePayload, ICustomer} from "@mytour/shared";
import {NoCustomerNotification} from "@mytour/ui/NoCustomerNotification";
import {FloatingField} from "@mytour/ui/form";
import {useState} from "react";
import type {Control, FieldErrors} from "react-hook-form";
import {Alert, Button, FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";
import {ApplianceRecapCard} from "../../components/ApplianceRecapCard";

export const sendCertificateEmailSchema = z.object({
  email: z
    .string()
    .refine((value) => value === "" || z.string().email().safeParse(value).success, {
      message: "Adresse email invalide",
    }),
});
export type SendCertificateEmailFormData = z.infer<typeof sendCertificateEmailSchema>;

type Props = {
  customer: ICustomer;
  data: CertificatRamonagePayload;
  sendEmail: boolean;
  onToggleSendEmail: () => void;
  onDownloadPreview: () => Promise<void>;
  disabled: boolean;
  canEditCustomer: boolean;
  emailControl: Control<SendCertificateEmailFormData>;
  emailErrors: FieldErrors<SendCertificateEmailFormData>;
};

export function SendCertificateStep({
  customer,
  data,
  sendEmail,
  onToggleSendEmail,
  onDownloadPreview,
  disabled,
  canEditCustomer,
  emailControl,
  emailErrors,
}: Props) {
  const [downloading, setDownloading] = useState(false);

  return (
    <>
      <h5 style={{marginBottom: "16px"}}>Récapitulatif et envoi</h5>

      <ApplianceRecapCard appareils={data.appareils} />

      <Button
        style={{marginBottom: "20px"}}
        color="primary"
        onClick={handleDownloadPreview}
        disabled={disabled || downloading}
      >
        <FontAwesomeIcon icon="file-pdf" />
        &nbsp; Prévisualiser l'attestation
      </Button>

      {customer?.email ? (
        <>
          <FormGroup check>
            <Input
              type="checkbox"
              id="field-sendEmail"
              checked={sendEmail}
              onChange={onToggleSendEmail}
              disabled={disabled}
            />
            <Label check for="field-sendEmail">
              Envoyer par email au client
            </Label>
          </FormGroup>
          {sendEmail ? (
            <p style={{fontSize: "14px", marginTop: "8px"}}>
              Un email avec l'attestation sera envoyé à{" "}
              <a href={`mailto:${customer.email}`}>{customer.email}</a>.
            </p>
          ) : (
            <p style={{fontSize: "14px", marginTop: "8px"}}>
              L&apos;attestation sera enregistrée dans l&apos;application.
              <br />
              <strong>Le client ne recevra pas d&apos;email.</strong>
            </p>
          )}
        </>
      ) : canEditCustomer ? (
        <>
          <Alert color="warning">
            {getLabel(customer)}
            <b>{" n'a aucun email"}</b>
            {" renseigné. Vous pouvez en ajouter un ci-dessous pour lui envoyer l'attestation."}
          </Alert>
          <FloatingField
            name="email"
            label="Adresse email"
            control={emailControl}
            error={emailErrors.email}
            disabled={disabled}
          />
        </>
      ) : (
        <NoCustomerNotification customer={customer} />
      )}
    </>
  );

  async function handleDownloadPreview() {
    try {
      setDownloading(true);
      await onDownloadPreview();
    } finally {
      setDownloading(false);
    }
  }
}
